import { formatClockText, getTimeFormat } from '@/shared/lib/time-format';
import { specialityDisplay } from '@/scheduling/lib/calendar-mapping';
import { PickerGrid } from '@/scheduling/components/shared/picker-grid';
import { pickerInitials, type PickerCardItem } from '@/scheduling/lib/picker-cards';
import type { ProviderSlotsResponse } from '@/services/scheduling/use-provider-slots';

/**
 * ProviderPickerGrid — shared 2-column provider picker used by both:
 *   - the create-appointment panel (`provider-section.tsx`)
 *   - the block-availability panel (`block-availability-provider-search.tsx`)
 *
 * Behavior matches the appointment panel's original inline picker
 * (the canonical reference): collapsed at 4 cards with a chevron
 * toggle, selected provider replaces the grid with a brand-primary
 * card showing the × clear button. Search filtering is the caller's
 * responsibility — pass the already-filtered `providers` list so
 * each consumer owns its server-vs-client filter strategy.
 *
 * Reuse rationale: previously the two panels diverged subtly (block
 * panel rendered a single-column list, missed the chevron toggle).
 *
 * Since PROD-2193 this is an ADAPTER. The card / search / collapse markup
 * lives in the entity-agnostic `PickerGrid`; this file owns only what is
 * provider-specific — the `Dr.` honorific rule, `specialityDisplay`, the
 * working-hours line and the provider-worded copy. `DevicePickerGrid` is the
 * sibling adapter for devices, so the two controls cannot drift apart. The
 * rendered DOM is unchanged by that move.
 */

function formatWorkingHours(hours: { startTime?: string; endTime?: string }[] | undefined): string {
  if (!hours || hours.length === 0) return 'Non working';
  // Server sends "09:00 AM": 12-hour keeps the compact "09:00a", 24-hour reads "09:00".
  const compact = (t?: string) =>
    !t ? '' : getTimeFormat() === '12h' ? t.toLowerCase().slice(0, -1).replace(/\s+/g, '') : formatClockText(t);
  const start = compact(hours[0].startTime);
  const end = compact(hours[0].endTime);
  if (!start || !end) return 'Non working';
  return `${start} - ${end}`;
}

/** How the app writes a practitioner's name, honorific normalised. */
function providerName(name: string | null | undefined): string {
  return (name ?? 'Unknown').replace(/^Dr\.?\s*/i, 'Dr. ');
}

/**
 * Minimal projection of `ProviderSlotsResponse` for the SELECTED state.
 * The block panel and appointment panel both have different "selected"
 * shapes (BlockPractitioner vs RHF form values), so the picker takes
 * the small subset it needs.
 */
export interface SelectedProviderInfo {
  id: string;
  name: string;
  photo?: string | null;
  speciality?: string | null;
}

interface Props {
  /** The full provider list to render — caller is responsible for
   *  any filtering / search narrowing before passing in. */
  providers: ProviderSlotsResponse[];
  /** Currently-selected provider (replaces the grid). */
  selected: SelectedProviderInfo | null;
  isLoading: boolean;
  onSelect: (provider: ProviderSlotsResponse) => void;
  onClear: () => void;
  /** Bound search input (controlled). Pass `null` to hide the input
   *  (e.g. when the consumer wants its own search affordance). */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  /** Hide the search input entirely. Default false. */
  hideSearch?: boolean;
  /** Number of cards shown before the chevron clamps the list.
   *  Default 4 (matches the appointment panel's COLLAPSED_COUNT). */
  collapsedCount?: number;
  /** Override the empty / no-match placeholder text. */
  emptyText?: string;
  /** Override the searching-with-no-results placeholder text. */
  noMatchText?: string;
  /**
   * View-only mode. Hides the × clear button on the selected card. Used by
   * the block-availability panel in edit mode (PR 5) where the user can only
   * see which provider the existing block belongs to.
   */
  readOnly?: boolean;
}

export function ProviderPickerGrid({
  providers,
  selected,
  isLoading,
  onSelect,
  onClear,
  searchValue = '',
  onSearchChange,
  hideSearch = false,
  collapsedCount = 4,
  emptyText = 'No providers available for this date.',
  noMatchText = 'No providers match this search.',
  readOnly = false,
}: Props) {
  const items: PickerCardItem[] = providers.map((p) => ({
    id: p.id,
    title: providerName(p.name),
    subtitle: specialityDisplay(p.speciality),
    meta: formatWorkingHours(p.workingHours),
    photo: p.photo,
    initials: pickerInitials(p.name ?? ''),
  }));

  const selectedItem: PickerCardItem | null = selected
    ? {
        id: selected.id,
        title: providerName(selected.name),
        subtitle: selected.speciality,
        photo: selected.photo,
        initials: pickerInitials(selected.name),
      }
    : null;

  return (
    <PickerGrid
      items={items}
      selected={selectedItem}
      isLoading={isLoading}
      // By INDEX, not id: a provider row can arrive with a blank id, and the
      // pre-extraction grid keyed those by position for the same reason.
      onSelect={(_item, i) => onSelect(providers[i])}
      onClear={onClear}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      searchPlaceholder="Search provider by name or speciality"
      hideSearch={hideSearch}
      collapsedCount={collapsedCount}
      emptyText={emptyText}
      noMatchText={noMatchText}
      clearLabel="Clear selected provider"
      expandLabel="Show more providers"
      readOnly={readOnly}
    />
  );
}
